import { useState, useCallback, useRef, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { PillInput } from 'src/components/common/pills/PillInput';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  FloppyDiskIcon,
  CircleNotchIcon,
  ArrowUUpLeftIcon,
  PencilIcon,
  WarningIcon,
  ArrowLeftIcon,
  CaretDownIcon,
  TreeStructureIcon,
  ExportIcon,
  UploadSimpleIcon,
  CopyIcon,
  DownloadIcon,
  ArrowCounterClockwiseIcon,
  ArrowClockwiseIcon,
  RocketLaunchIcon,
  TrashSimpleIcon,
  DotsThreeVerticalIcon,
  ListBulletsIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';
import { Watchers } from 'src/components/watchers/Watchers';
import { VersionPickerDropdown } from './VersionPickerDropdown';

function DetailsSummary({
  description,
  tags,
}: {
  description: string;
  tags: string[];
}) {
  const parts: string[] = [];

  if (description.trim()) {
    const truncated =
      description.length > 50
        ? description.slice(0, 50).trimEnd() + '…'
        : description;
    parts.push(truncated);
  } else {
    parts.push('No description');
  }

  if (tags.length > 0) {
    parts.push(tags.length === 1 ? '1 tag' : `${tags.length} tags`);
  }

  return <span className="truncate">{parts.join(' · ')}</span>;
}

interface WorkflowEditorHeaderProps {
  workflowName: string;
  workflowDescription: string;
  workflowTags: string[];
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onTagsChange: (tags: string[]) => void;
  isCreateMode: boolean;
  isSaving: boolean;
  isDirty: boolean;
  hasTrigger: boolean;
  hasScheduleTrigger: boolean;
  onSave: () => void;
  /** Create-mode only: full-validation create. When omitted, only the Save Draft button is shown in create mode. */
  onCreate?: () => void;
  onReset: () => void;
  onCancel: () => void;
  onAutoLayout: () => void;
  onExportDownload: () => void;
  onExportCopy: () => void;
  onImport: (file: File) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  // Versioning — optional; only rendered when an existing definition is loaded.
  // `versionStatus` distinguishes a mutable draft from a frozen finalized version.
  versionStatus?: 'draft' | 'finalized';
  versionNumber?: number;
  isPinned?: boolean;
  // For the picker dropdown in the header action row.
  lineageId?: string;
  currentVersionId?: string;
  onOpenVersionHistory?: () => void;
  onCreateDraft?: () => void;
  isCreatingDraft?: boolean;
  // When the current user already has an open draft on this workflow, the
  // "Create new draft" button is replaced with a "Resume draft vN" button
  // that navigates to it. Backend enforces one-draft-per-user-per-workflow;
  // this makes the redirect discoverable instead of surprising.
  resumableDraft?: {
    version: number;
    onResume: () => void;
  };
  onPublish?: () => void;
  isPublishing?: boolean;
  onDiscardDraft?: () => void;
  isDiscardingDraft?: boolean;
  // When true, the header hides all mutation-related controls (Save, Reset,
  // Cancel-is-back, Undo/Redo, Auto Layout, Import/Export,
  // Editing/Unsaved pills). The user is inspecting a version they can't edit.
  isReadOnly?: boolean;
  /** Preserve `?view=graph` when switching versions in the version picker. */
  stayInGraphView?: boolean;
  /** Overflow menu — delete the whole workflow lineage. Omit to hide. */
  onDeleteWorkflow?: () => void;
  /** Overflow menu — navigate to the workflow's runs page. Omit to hide. */
  onViewRuns?: () => void;
  /** Workflow lineage ID for the Watchers component. */
  workflowId?: string;
}

export function WorkflowEditorHeader({
  workflowName,
  workflowDescription,
  workflowTags,
  onNameChange,
  onDescriptionChange,
  onTagsChange,
  isCreateMode,
  isSaving,
  isDirty,
  hasTrigger,
  hasScheduleTrigger,
  onSave,
  onCreate,
  onReset,
  onCancel,
  onAutoLayout,
  onExportDownload,
  onExportCopy,
  onImport,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  versionStatus,
  versionNumber,
  isPinned,
  lineageId,
  currentVersionId,
  onOpenVersionHistory,
  onCreateDraft,
  isCreatingDraft,
  onPublish,
  isPublishing,
  onDiscardDraft,
  isDiscardingDraft,
  resumableDraft,
  isReadOnly = false,
  stayInGraphView = false,
  onDeleteWorkflow,
  onViewRuns,
  workflowId,
}: WorkflowEditorHeaderProps) {
  const gt = useGT();
  const [isEditingName, setIsEditingName] = useState(isCreateMode);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onImport(file);
      }
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [onImport],
  );

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        setIsEditingName(false);
      } else if (e.key === 'Escape') {
        setIsEditingName(false);
      }
    },
    [],
  );

  const handleNameBlur = useCallback(() => {
    setIsEditingName(false);
  }, []);

  const handleDetailsToggle = useCallback(() => {
    setIsDetailsOpen((open) => !open);
  }, []);

  return (
    <div className="flex-shrink-0 border-b bg-background">
      {!hasTrigger && (
        <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <WarningIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          <span className="text-xs text-amber-700 dark:text-amber-300">
            <T>No trigger - workflow won't run automatically</T>
          </span>
        </div>
      )}

      <div className="flex items-center px-4 pb-1.5">
        <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={onCancel}
            disabled={isSaving}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>

          {isReadOnly ? (
            <span className="text-lg font-semibold truncate max-w-[300px]">
              {workflowName || <T>Untitled Workflow</T>}
            </span>
          ) : isEditingName ? (
            <Input
              ref={nameInputRef}
              value={workflowName}
              onChange={(e) => onNameChange(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={handleNameBlur}
              placeholder={gt('Workflow name...', {
                context: 'workflow name input placeholder',
              })}
              disabled={isSaving}
              className={cn(
                'text-lg font-semibold h-8 max-w-[300px]',
                isCreateMode && !workflowName.trim() && 'border-destructive',
              )}
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsEditingName(true)}
              disabled={isSaving}
              className={cn(
                'flex items-center gap-2 text-lg font-semibold truncate',
                'hover:text-primary transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span className="truncate max-w-[300px]">
                {workflowName || <T>Untitled Workflow</T>}
              </span>
              <PencilIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </button>
          )}

          <button
            type="button"
            onClick={handleDetailsToggle}
            disabled={isSaving}
            className={cn(
              'flex items-center gap-1 px-2 py-0.5 rounded text-xs text-muted-foreground',
              'hover:text-foreground hover:bg-muted transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'min-w-0 overflow-hidden',
            )}
          >
            <CaretDownIcon
              className={cn(
                'w-3 h-3 flex-shrink-0 transition-transform',
                isDetailsOpen ? 'rotate-0' : '-rotate-90',
              )}
            />
            <DetailsSummary
              description={workflowDescription}
              tags={workflowTags}
            />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 flex-1 justify-end">
          {!isReadOnly && isDirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
              <T>Unsaved</T>
            </span>
          )}
          {!isReadOnly && (
            <Badge variant="secondary" className="text-xs">
              {isCreateMode ? <T>Draft</T> : <T>Editing</T>}
            </Badge>
          )}
          {!isReadOnly && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={onUndo}
                disabled={!canUndo || isSaving}
                className="h-8 w-8"
                aria-label={gt('Undo', { context: 'undo button aria-label' })}
                title={gt('Undo (Ctrl+Z)', { context: 'undo button tooltip' })}
              >
                <ArrowCounterClockwiseIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onRedo}
                disabled={!canRedo || isSaving}
                className="h-8 w-8"
                aria-label={gt('Redo', { context: 'redo button aria-label' })}
                title={gt('Redo (Ctrl+Shift+Z)', {
                  context: 'redo button tooltip',
                })}
              >
                <ArrowClockwiseIcon className="w-4 h-4" />
              </Button>
              <div className="w-px h-5 bg-border mx-1" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleImportClick}
                disabled={isSaving}
                className="h-8"
                title={gt('Import workflow from JSON', {
                  context: 'import button tooltip',
                })}
              >
                <UploadSimpleIcon className="w-4 h-4 mr-1" />
                <T>Import</T>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isSaving}
                    className="h-8"
                    title={gt('Export workflow as JSON', {
                      context: 'export button tooltip',
                    })}
                  >
                    <ExportIcon className="w-4 h-4 mr-1" />
                    <T>Export</T>
                    <CaretDownIcon className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onExportCopy}>
                    <CopyIcon className="w-4 h-4 mr-2" />
                    <T>Copy JSON to clipboard</T>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportDownload}>
                    <DownloadIcon className="w-4 h-4 mr-2" />
                    <T>Download as file</T>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="sm"
                onClick={onAutoLayout}
                disabled={isSaving}
                className="h-8"
                title={gt('Auto-layout nodes', {
                  context: 'auto layout button tooltip',
                })}
              >
                <TreeStructureIcon className="w-4 h-4 mr-1" />
                <T>Auto Layout</T>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onReset}
                disabled={!isDirty || isSaving}
                className="h-8"
              >
                <ArrowUUpLeftIcon className="w-4 h-4 mr-1" />
                <T>Reset</T>
              </Button>
            </>
          )}
          {!isCreateMode && versionStatus && (
            <>
              {/* Version picker — click to switch to another version, with
                  an escape hatch to the full history dialog. */}
              {lineageId && currentVersionId && onOpenVersionHistory && (
                <VersionPickerDropdown
                  workflowId={lineageId}
                  currentVersionId={currentVersionId}
                  currentVersionNumber={versionNumber ?? 0}
                  currentStatus={versionStatus}
                  currentIsPinned={isPinned ?? false}
                  onOpenFullHistory={onOpenVersionHistory}
                  stayInEditor={!stayInGraphView && !isReadOnly}
                  stayInGraphView={stayInGraphView || isReadOnly}
                  versionSwitchDisabled={versionStatus === 'draft'}
                />
              )}
              {/* Fallback when auto-bootstrap did not run (e.g. create failed).
                  Backend enforces one draft per user per workflow. */}
              {versionStatus === 'finalized' && resumableDraft && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resumableDraft.onResume}
                  disabled={isSaving}
                  className="h-8"
                  title={gt('Continue editing your existing draft v{version}', {
                    context: 'resume draft button tooltip',
                    version: String(resumableDraft.version),
                  })}
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  <T>
                    Resume draft v<Var>{resumableDraft.version}</Var>
                  </T>
                </Button>
              )}
              {versionStatus === 'finalized' &&
                !resumableDraft &&
                onCreateDraft && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCreateDraft}
                    disabled={isCreatingDraft || isSaving}
                    className="h-8"
                    title={gt('Create a new draft forked from this version', {
                      context: 'create draft button tooltip',
                    })}
                  >
                    {isCreatingDraft ? (
                      <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <PencilIcon className="w-4 h-4 mr-1" />
                    )}
                    <T>Create new draft</T>
                  </Button>
                )}
            </>
          )}
          {!isReadOnly && onDiscardDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              disabled={isSaving || isPublishing || isDiscardingDraft}
              className="h-8 border-destructive text-destructive hover:text-destructive hover:bg-destructive/10"
              title={gt('Discard this draft', {
                context: 'discard draft button tooltip',
              })}
            >
              {isDiscardingDraft ? (
                <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <TrashSimpleIcon className="w-4 h-4 mr-1" />
              )}
              <T>Discard</T>
            </Button>
          )}
          {!isReadOnly && (
            <Button
              variant={
                isCreateMode && onCreate
                  ? 'outline'
                  : onPublish
                    ? 'outline'
                    : 'default'
              }
              size="sm"
              onClick={onSave}
              disabled={
                !isDirty ||
                isSaving ||
                isPublishing ||
                versionStatus === 'finalized'
              }
              className="h-8"
            >
              {isSaving && !isPublishing ? (
                <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <FloppyDiskIcon className="w-4 h-4 mr-1" />
              )}
              {isCreateMode ? <T>Save Draft</T> : <T>Save</T>}
            </Button>
          )}
          {!isReadOnly && isCreateMode && onCreate && (
            <Button
              size="sm"
              onClick={onCreate}
              disabled={!isDirty || isSaving || isPublishing}
              className="h-8"
            >
              {isSaving && !isPublishing ? (
                <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <FloppyDiskIcon className="w-4 h-4 mr-1" />
              )}
              <T>Create</T>
            </Button>
          )}
          {!isReadOnly && onPublish && (
            <Button
              size="sm"
              onClick={onPublish}
              disabled={
                isSaving || isPublishing || versionStatus === 'finalized'
              }
              className="h-8"
            >
              {isPublishing ? (
                <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <RocketLaunchIcon className="w-4 h-4 mr-1" />
              )}
              <T>Save & Publish</T>
            </Button>
          )}
          {workflowId && (
            <>
              <div className="w-px h-5 bg-border mx-1" />
              <Watchers
                watchableEntityType="workflow"
                watchableId={workflowId}
                size="sm"
              />
            </>
          )}
          {(onViewRuns || onDeleteWorkflow) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={isSaving || isPublishing}
                  aria-label={gt('More actions', {
                    context: 'workflow editor overflow menu aria-label',
                  })}
                  title={gt('More actions', {
                    context: 'workflow editor overflow menu tooltip',
                  })}
                >
                  <DotsThreeVerticalIcon className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onViewRuns && (
                  <DropdownMenuItem onClick={onViewRuns}>
                    <ListBulletsIcon className="w-4 h-4 mr-2" />
                    <T>View runs</T>
                  </DropdownMenuItem>
                )}
                {onDeleteWorkflow && (
                  <DropdownMenuItem
                    onClick={onDeleteWorkflow}
                    className="text-destructive focus:text-destructive"
                  >
                    <TrashSimpleIcon className="w-4 h-4 mr-2" />
                    <T>Delete workflow</T>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {isDetailsOpen && (
        <div className="px-4 pb-3 pt-1 border-t">
          <div className="grid gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                <T>Description</T>
              </label>
              {isReadOnly ? (
                <p className="mt-1.5 text-sm text-foreground whitespace-pre-wrap">
                  {workflowDescription.trim() ? (
                    workflowDescription
                  ) : (
                    <span className="text-muted-foreground">
                      <T context="workflow editor read-only empty description">
                        No description
                      </T>
                    </span>
                  )}
                </p>
              ) : (
                <Textarea
                  value={workflowDescription}
                  onChange={(e) => onDescriptionChange(e.target.value)}
                  placeholder={gt('Add a description...', {
                    context: 'workflow description placeholder',
                  })}
                  disabled={isSaving}
                  rows={2}
                  className="mt-1.5 text-sm resize-none"
                />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                <T>Tags</T>
              </label>
              {isReadOnly ? (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {workflowTags.length > 0 ? (
                    workflowTags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="text-xs font-normal"
                      >
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      <T context="workflow editor read-only empty tags">
                        No tags
                      </T>
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-1.5">
                  <PillInput
                    values={workflowTags}
                    onChange={onTagsChange}
                    placeholder={gt('Add tags...', {
                      context: 'tags input placeholder',
                    })}
                    className="text-xs"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
